import { readLogContext } from '#@/context.ts'
import { isLevel, type Level, passesLevel } from '#@/levels.ts'
import { buildRecord, type LogFields } from '#@/record.ts'
import { isLogFormat, type LogFormat } from '#@/runtime/Runtime.ts'
import * as runtime from '#runtime'

export type Logger = {
  debug: (...args: unknown[]) => void
  info: (...args: unknown[]) => void
  warn: (...args: unknown[]) => void
  error: (...args: unknown[]) => void
  /** A logger with additional prefixes. It retains bound fields (R7). */
  logger: (...additionalPrefixes: (string | string[])[]) => Logger
  /** A logger that adds `fields` to its records. Repeated calls merge shallowly; later fields take precedence (R7). */
  with: (fields: LogFields) => Logger
}

/**
 * Creates a logger named by its prefixes. Each call `log.<level>(message, ...context)` emits one record: a plain object
 * in the structured format, a coloured line in the pretty format. A plain object in `context` merges into the record, a
 * primitive appends to `args`, and an `Error` is serialised under `error` (R6). Identity fields such as an organization
 * id belong in `withLogContext`, not in a module-level `.with()`: a bound field overrides the context field (R9), so a
 * module-level identity would be stale for the next request.
 */
export function logger(...prefixes: (string | string[])[]): Logger {
  return createLogger(prefixes.flat(), {})
}

export type LoggerConfig = { level: Level; format: LogFormat }

/**
 * Overrides the level or format for every record after the call (R15). The environment is read on the first record and
 * cached; an override replaces the cached value until the next `configureLogger` call. A browser entry uses it to raise
 * the minimum level, because the browser variant reads no environment.
 */
export function configureLogger(overrides: Partial<LoggerConfig>): void {
  config = { ...resolveConfig(), ...overrides }
}

export { setLogContextStorage, withLogContext, type LogContextStorage } from '#@/context.ts'
export type { Level } from '#@/levels.ts'
export type { SerializedError } from '#@/normalizeValue.ts'
export type { LogFields, LogRecord } from '#@/record.ts'
export type { LogFormat } from '#@/runtime/Runtime.ts'

function createLogger(prefixes: string[], boundFields: LogFields): Logger {
  const name = prefixes.join('.')
  function at(level: Level): (...args: unknown[]) => void {
    return (...args) => {
      emitRecord({ level, name, prefixes, boundFields, args })
    }
  }
  return {
    debug: at('debug'),
    info: at('info'),
    warn: at('warn'),
    error: at('error'),
    logger: (...additionalPrefixes) => createLogger([...prefixes, ...additionalPrefixes.flat()], boundFields),
    with: (fields) => createLogger(prefixes, { ...boundFields, ...fields }),
  }
}

type EmitRecordArgs = { level: Level; name: string; prefixes: string[]; boundFields: LogFields; args: unknown[] }

function emitRecord({ level, name, prefixes, boundFields, args }: EmitRecordArgs): void {
  try {
    const { level: minimum, format } = resolveConfig()
    if (!passesLevel(level, minimum)) {
      // Below the minimum nothing reads `args` (R3).
      return
    }
    const record = buildRecord({ loggerName: name, contextFields: readLogContext(), boundFields, callArgs: args })
    runtime.emit({ level, record, prefixes, format })
  } catch {
    // R11: a log call never throws. `buildRecord` already reduced a normalisation failure to a record, so what
    // reaches here is a console method that threw, and the line is lost.
  }
}

function resolveConfig(): LoggerConfig {
  if (config !== undefined) {
    return config
  }
  const defaults = guarded(runtime.defaults) ?? { level: 'info', format: 'json' }
  const raw = guarded(runtime.readConfig) ?? { level: undefined, format: undefined }
  config = {
    level: parseSetting({ name: 'LOG_LEVEL', raw: raw.level, isValid: isLevel, fallback: defaults.level }),
    format: parseSetting({ name: 'LOG_FORMAT', raw: raw.format, isValid: isLogFormat, fallback: defaults.format }),
  }
  return config
}

type ParseSettingArgs<T extends string> = {
  name: string
  raw: string | undefined
  isValid: (value: unknown) => value is T
  fallback: T
}

function parseSetting<T extends string>({ name, raw, isValid, fallback }: ParseSettingArgs<T>): T {
  if (raw === undefined || raw === '') {
    return fallback
  }
  if (isValid(raw)) {
    return raw
  }
  warnOnce(`${name}=${JSON.stringify(raw)} is not supported; using ${JSON.stringify(fallback)}`)
  return fallback
}

/**
 * Emits one warning per module instance and message, as `tracerSeam.ts` does. A warning for every record increases
 * console output.
 */
function warnOnce(message: string): void {
  if (warned.has(message)) {
    return
  }
  warned.add(message)
  try {
    // oxlint-disable-next-line no-console -- the logger cannot report its own configuration failure through itself
    console.warn(`[logger] ${message}`)
  } catch {
    // R11: the warning is advisory.
  }
}

function guarded<T>(read: () => T): T | undefined {
  try {
    return read()
  } catch {
    return undefined
  }
}

let config: LoggerConfig | undefined = undefined
const warned = new Set<string>()
